import { useAtom } from "jotai"
import {
  isFullPageModalOpenAtom,
  isSubscribeModalOpenAtom,
} from "@/app/contexts/Atoms"
import { useRouter } from "next/navigation"
import useBaseBlogUrl from "@/app/hooks/useBaseBlogUrl"
import { useEffect } from "react"
import { usePromiseRouter } from "@/app/hooks/usePromiseRouter"

/**
 * Helper function to dismiss subscribe modals.
 *
 * This dismisses both the full-page modal and the intercepting-route modal,
 * by pushing the user back to the base blog URL.
 */
export default function useDismissModal(callerFuncName: string) {
  /** Full page modal uses Jotai.
   */
  const [isFullPageModalOpen, setIsFullPageModalOpen] = useAtom(
    isFullPageModalOpenAtom
  )
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useAtom(
    isSubscribeModalOpenAtom
  )
  const baseBlogUrl = useBaseBlogUrl()
  const router = useRouter()
  const promiseRouter = usePromiseRouter()

  useEffect(() => {
    router.prefetch(baseBlogUrl)
  }, [router, baseBlogUrl])

  const closeModal = async () => {
    // Go back to the previous page. This assumes that all stages of the subscribe modal were using
    // router.replace() rather than router.push(), so that the previous page is always the page the
    // reader started subscribing from.

    // If this was a full page modal, push to the @blog page, which closes this dialog.
    if (isFullPageModalOpen) {
      console.log("Full page modal was open, replacing path with blog url.", {
        baseBlogUrl,
        callerFuncName,
      })

      // Close the full-page modal, so the user doesn't need to also click through another modal
      // And close the inner modal too.
      setIsFullPageModalOpen(false)
      setIsSubscribeModalOpen(false)

      await promiseRouter.replace(`/${baseBlogUrl}`, { scroll: false })

      /**
       * We do this here to refresh and to properly update the Subscribe button and user avatar dropdown once the user has
       * logged in and subscribed. We can only do this after navigating back, as you can't refresh while the router is
       * navigating.
       *
       * NOTE: This sometimes doesn't get called if the component that housed the useDismissModal gets dismounted, in which
       * case you may need to call router.refresh() from the unmount hook for that component. See this component for an example
       * packages/nextjs/app/components/SubscribeModal/Success/Form.tsx
       */
      router.refresh()
    } else {
      console.log(
        "Regular subscribe modal was open, closing and navigating back.",
        { baseBlogUrl, callerFuncName }
      )

      // Otherwise, if the user clicked on a subscribe CTA button, push back to the previous URL, which closes this dialog.
      setIsSubscribeModalOpen(false)

      await promiseRouter.back()

      /**
       * We do this here to refresh and to properly update the Subscribe button and user avatar dropdown once the user has
       * logged in and subscribed. We can only do this after navigating back, as you can't refresh while the router is
       * navigating.
       */
      router.refresh()
    }
  }

  return closeModal
}
